/**
 * Value coercion used when decoded RPE data becomes IR (W1-07a formats-json-rpe). The decoder keeps values as
 * written and reports what Phira would reject; these helpers produce the nearest value the IR and the writer
 * accept (integers inside their Rust range, finite f32-representable floats, u8 colour channels).
 */

import { roundHalfAwayFromZero } from '../../core/ease/bezierKey';

/** Largest finite f32. */
export const F32_MAX = 3.4028234663852886e38;

/**
 * Integer inside [lo, hi]: rounded half away from zero (Rust `f32::round`), clamped; `def` for undefined / NaN.
 * Infinities clamp to the bounds.
 */
export function toInt(v: number | undefined, lo: number, hi: number, def: number): number {
  if (v === undefined || Number.isNaN(v)) return def;
  if (v === Infinity) return hi;
  if (v === -Infinity) return lo;
  const r = Number.isInteger(v) ? v : roundHalfAwayFromZero(v);
  const c = r < lo ? lo : r > hi ? hi : r;
  return c === 0 ? 0 : c;
}

/** A finite value whose f32 rounding is finite (values beyond the f32 range clamp to +-F32_MAX). */
export function clampF32(v: number): number {
  if (Number.isNaN(v)) return 0;
  if (v > F32_MAX) return F32_MAX;
  if (v < -F32_MAX) return -F32_MAX;
  return v;
}

/** A finite number that f32 cannot hold (Phira's f32 fields read it as an infinity). */
export function exceedsF32(v: number): boolean {
  return Number.isFinite(v) && !Number.isFinite(Math.fround(v));
}
